import { apiClient } from "@/api/request";

export enum SkillStatus {
  UNAVAILABLE = 0,
  AVAILABLE = 1,
  UPLOADING = 2,
}
export interface SkillFile {
  path: string;
  isBinary: boolean;
  size: number;
  mimeType: string;
  sha256: string;
  updatedAt: string;
}
export interface SkillFileContent {
  file?: SkillFile;
  content: string;
  encoding: string;
}
export interface SkillVersion {
  versionCode: number;
  description: string;
  content: string;
  frontmatter: Record<string, unknown>;
  isLatest: boolean;
  isStable: boolean;
  branch: string;
  createdBy: string;
  storagePath: string;
  status: string;
  entrypoint: string;
  contentHash: string;
  fileCount: number;
  createdAt: string;
  updatedAt: string;
}
export interface Skill {
  id: string;
  name: string;
  description: string;
  author: string;
  userId: string;
  tenantId: string;
  status: SkillStatus;
  tags: string[];
  versions: SkillVersion[];
  createdAt: string;
  updatedAt: string;
  filePath: string;
}
type JsonObject = Record<string, unknown>;
const text = (value: unknown) => (typeof value === "string" ? value : "");
function version(raw: JsonObject): SkillVersion {
  return {
    versionCode: Number(raw.version_code ?? 0),
    description: text(raw.description),
    content: text(raw.content),
    frontmatter: (raw.frontmatter ?? {}) as Record<string, unknown>,
    isLatest: Boolean(raw.is_latest),
    isStable: Boolean(raw.is_stable),
    branch: text(raw.branch),
    createdBy: text(raw.created_by),
    storagePath: text(raw.storage_path),
    status: text(raw.status),
    entrypoint: text(raw.entrypoint),
    contentHash: text(raw.content_hash),
    fileCount: Number(raw.file_count ?? 0),
    createdAt: text(raw.created_at),
    updatedAt: text(raw.updated_at),
  };
}
function skill(raw: JsonObject): Skill {
  return {
    id: text(raw.id),
    name: text(raw.name),
    description: text(raw.description),
    author: text(raw.author),
    userId: text(raw.user_id),
    tenantId: text(raw.tenant_id),
    status:
      typeof raw.status === "string" && raw.status in SkillStatus
        ? (SkillStatus[raw.status as keyof typeof SkillStatus] as SkillStatus)
        : Number(raw.status ?? 0),
    tags: (raw.tags ?? []) as string[],
    versions: ((raw.versions ?? []) as JsonObject[]).map(version),
    createdAt: text(raw.created_at),
    updatedAt: text(raw.updated_at),
    filePath: text(raw.file_path),
  };
}
function file(raw: JsonObject): SkillFile {
  return {
    path: text(raw.path),
    isBinary: Boolean(raw.is_binary),
    size: Number(raw.size ?? 0),
    mimeType: text(raw.mime_type),
    sha256: text(raw.sha256),
    updatedAt: text(raw.updated_at),
  };
}
async function send(path: string, body: JsonObject): Promise<JsonObject> {
  return (await apiClient.post<JsonObject>("/v2/" + path, body)).data;
}
async function data(path: string, body: JsonObject): Promise<JsonObject> {
  return (await send(path, body)).data as JsonObject;
}
const target = (skillId: string, versionCode: number) => ({
  skill_id: skillId,
  version_code: versionCode,
});
const decode = (base64: string) => Uint8Array.from(atob(base64), value => value.charCodeAt(0));
const encode = (bytes: Uint8Array) => {
  let value = "";
  for (const byte of bytes) value += String.fromCharCode(byte);
  return btoa(value);
};
export const skillApi = {
  listSkills: async (params: {
    page: number;
    pageSize: number;
    keyword?: string;
    withVersions?: boolean;
  }) => {
    const raw = await data("ListSkills", {
      page: params.page,
      page_size: params.pageSize,
      fuzzy: params.keyword ? { name: params.keyword } : {},
      filter: params.withVersions ? { with_versions: "true" } : {},
    });
    return { data: ((raw.items ?? []) as JsonObject[]).map(skill), total: Number(raw.total ?? 0) };
  },
  getSkill: async (skillId: string, withVersions = false) =>
    skill(await data("GetSkill", { skill_id: skillId, with_versions: withVersions })),
  createSkill: async (payload: {
    request: {
      name: string;
      description?: string;
      initial_version_description?: string;
      status?: number;
      tags?: string[];
      content?: string;
    };
  }) => skill(await data("CreateSkill", payload)),
  updateSkill: async (
    skillId: string,
    payload: { request: { name?: string; description?: string; status?: number } },
  ) => skill(await data("UpdateSkill", { skill_id: skillId, ...payload })),
  deleteSkill: async (skillId: string) => {
    await send("DeleteSkill", { skill_id: skillId });
  },
  getSkillVersion: async (skillId: string, code: number, include = "content") =>
    version(await data("GetSkillVersion", { ...target(skillId, code), include })),
  createSkillVersion: async (
    skillId: string,
    request: {
      description?: string;
      branch?: string;
      createdBy?: string;
      isStable?: boolean;
    } & (
      | { sourceVersionCode: number; content?: never }
      | { sourceVersionCode?: never; content?: string }
    ),
  ) =>
    version(
      await data("CreateSkillVersion", {
        skill_id: skillId,
        request: {
          source_version_code: request.sourceVersionCode,
          description: request.description,
          branch: request.branch,
          created_by: request.createdBy,
          is_stable: request.isStable,
          ...(request.sourceVersionCode === undefined ? { content: request.content } : {}),
        },
      }),
    ),
  updateSkillVersionMetadata: async (skillId: string, code: number, description: string) =>
    version(
      await data("UpdateSkillVersionMetadata", {
        ...target(skillId, code),
        request: { description },
      }),
    ),
  updateSkillVersionContent: async (skillId: string, code: number, content: string) =>
    version(
      await data("UpdateSkillVersionContent", { ...target(skillId, code), request: { content } }),
    ),
  cloneSkillVersion: async (skillId: string, code: number) =>
    version(await data("CloneSkillVersion", target(skillId, code))),
  promoteSkillVersion: async (
    skillId: string,
    code: number,
    request: { isLatest?: boolean; isStable?: boolean },
  ) =>
    version(
      await data("PromoteSkillVersion", {
        ...target(skillId, code),
        request: { is_latest: !!request.isLatest, is_stable: !!request.isStable },
      }),
    ),
  deleteSkillVersion: async (skillId: string, code: number) => {
    await send("DeleteSkillVersion", target(skillId, code));
  },
  listSkillVersionFiles: async (skillId: string, code: number) => {
    const raw = await data("ListSkillVersionFiles", target(skillId, code));
    return { files: ((raw.files ?? []) as JsonObject[]).map(file) };
  },
  getSkillVersionFile: async (
    skillId: string,
    code: number,
    path: string,
  ): Promise<SkillFileContent> => {
    const raw = await data("GetSkillVersionFile", { ...target(skillId, code), file_path: path });
    return {
      file: raw.file ? file(raw.file as JsonObject) : undefined,
      content: text(raw.content),
      encoding: text(raw.encoding),
    };
  },
  putSkillVersionFile: async (
    skillId: string,
    code: number,
    path: string,
    request: { content: string; encoding?: string },
  ) => {
    await send("PutSkillVersionFile", { ...target(skillId, code), file_path: path, request });
  },
  deleteSkillVersionFile: async (skillId: string, code: number, path: string) => {
    await send("DeleteSkillVersionFile", { ...target(skillId, code), file_path: path });
  },
  importSkillZip: async (zip: File, skillId?: string) => {
    const raw = await send("ImportSkillZip", {
      file_name: zip.name,
      file: encode(new Uint8Array(await zip.arrayBuffer())),
      skill_id: skillId,
    });
    const item = (raw.data ?? {}) as JsonObject;
    return {
      data: {
        skillId: text(item.skill_id),
        name: text(item.name),
        versionCode: Number(item.version_code ?? 0),
        created: Boolean(item.created),
      },
    };
  },
  exportSkillVersion: async (skillId: string, code: number) => {
    const raw = await data("ExportSkillVersion", target(skillId, code));
    return { data: decode(text(raw.data)) };
  },
};
